/**
 * Models Gallery Page
 * Displays comprehensive details for all models using the model-details API endpoint
 */

import React, { useState, useEffect } from 'react';
import '../styles/ModelsGallery.css';
import { API_CONFIG } from '../config/api';

const ModelsGallery = () => {
  const [modules, setModules] = useState([]);
  const [modelDetails, setModelDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedModels, setExpandedModels] = useState(new Set());

  useEffect(() => {
    fetchAllModels();
  }, []);

  const fetchAllModels = async () => {
    try {
      setLoading(true);

      // Fetch modules list
      const modulesResponse = await fetch(API_CONFIG.ENDPOINTS.MODULES);
      const modulesData = await modulesResponse.json();
      const modulesList = modulesData.modules || [];
      setModules(modulesList);

      // Fetch details for each module
      const detailsMap = {};
      for (const module of modulesList) {
        try {
          const detailResponse = await fetch(
            API_CONFIG.ENDPOINTS.MODEL_DETAILS(module.id)
          );
          const detailData = await detailResponse.json();
          detailsMap[module.id] = detailData.model || detailData;
        } catch (error) {
          console.error(`Error fetching details for ${module.id}:`, error);
          detailsMap[module.id] = null;
        }
      }
      setModelDetails(detailsMap);
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (moduleId) => {
    const newExpanded = new Set(expandedModels);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModels(newExpanded);
  };

  if (loading) {
    return (
      <div className="models-gallery">
        <div className="gallery-header">
          <h1>📚 Model Information Gallery</h1>
          <p>Comprehensive details for all ANN governance models</p>
        </div>
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading model information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="models-gallery">
      <div className="gallery-header">
        <h1>📚 Model Information Gallery</h1>
        <p>Comprehensive details for all ANN governance models</p>
        <div className="gallery-stats">
          <span className="stat-badge">{modules.length} Models</span>
        </div>
      </div>

      <div className="models-grid">
        {modules.map((module) => {
          const details = modelDetails[module.id];
          const isExpanded = expandedModels.has(module.id);

          return (
            <div
              key={module.id}
              className={`model-card ${isExpanded ? 'expanded' : ''}`}
              style={{ borderLeftColor: module.color }}
            >
              {/* Card Header */}
              <div
                className="model-card-header"
                onClick={() => toggleExpanded(module.id)}
              >
                <div className="model-card-title">
                  <span className="model-icon">{module.icon}</span>
                  <div className="model-info">
                    <h3>{module.name}</h3>
                    <p className="model-id">{module.id}</p>
                  </div>
                </div>
                <button className="expand-btn">
                  {isExpanded ? '▼' : '▶'}
                </button>
              </div>

              {/* Card Description */}
              <p className="model-description">{module.description}</p>

              {/* Status Badge */}
              <div className="model-status">
                <span className={`status-badge status-${module.status}`}>
                  {module.status}
                </span>
              </div>

              {/* Expanded Details */}
              {isExpanded && details && (
                <div className="model-details-section">
                  {/* Metrics */}
                  {details.metrics && Object.keys(details.metrics).length > 0 && (
                    <div className="details-subsection">
                      <h4>📊 Performance Metrics</h4>
                      <div className="metrics-grid">
                        {Object.entries(details.metrics).map(([key, value]) => (
                          <div key={key} className="metric-item">
                            <span className="metric-label">{key}</span>
                            <span className="metric-value">
                              {typeof value === 'number'
                                ? value.toFixed(4)
                                : value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Parameters */}
                  {details.parameters && Object.keys(details.parameters).length > 0 && (
                    <div className="details-subsection">
                      <h4>📋 Input Parameters</h4>
                      <div className="parameters-list">
                        {Object.entries(details.parameters).map(([key, param]) => (
                          <div key={key} className="parameter-item">
                            <div className="param-header">
                              <label>{param.label || key}</label>
                              {param.required && (
                                <span className="required-badge">Required</span>
                              )}
                            </div>
                            <p className="param-description">
                              {param.description}
                            </p>
                            <div className="param-meta">
                              <span className="param-type">
                                Type: {param.type}
                              </span>
                              {param.unit && (
                                <span className="param-unit">
                                  Unit: {param.unit}
                                </span>
                              )}
                              {param.min !== undefined && param.max !== undefined && (
                                <span className="param-range">
                                  Range: {param.min} - {param.max}
                                </span>
                              )}
                            </div>
                            {param.options && (
                              <div className="param-options">
                                <strong>Options:</strong>
                                <ul>
                                  {param.options.map((option) => (
                                    <li key={option}>{option}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Output Predictions */}
                  {details.predictions && Object.keys(details.predictions).length > 0 && (
                    <div className="details-subsection">
                      <h4>🔮 Output Predictions</h4>
                      <div className="predictions-list">
                        {Object.entries(details.predictions).map(([key, pred]) => (
                          <div key={key} className="prediction-item">
                            <div className="pred-header">
                              <label>{pred.label || key}</label>
                            </div>
                            <p className="pred-description">
                              {pred.description}
                            </p>
                            {pred.type && (
                              <span className="pred-type">
                                Type: {pred.type}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Generic Details */}
                  {details.data_features && (
                    <div className="details-subsection">
                      <h4>🎯 Data Features</h4>
                      <p>{details.data_features}</p>
                    </div>
                  )}

                  {details.model_architecture && (
                    <div className="details-subsection">
                      <h4>🏗️ Model Architecture</h4>
                      <p>{details.model_architecture}</p>
                    </div>
                  )}

                  {details.use_case && (
                    <div className="details-subsection">
                      <h4>💡 Use Case</h4>
                      <p>{details.use_case}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modules.length === 0 && !loading && (
        <div className="empty-state">
          <p>No models available</p>
        </div>
      )}
    </div>
  );
};

export default ModelsGallery;
